import React, { useState, useEffect, useMemo } from "react"
import Popup from "reactjs-popup"
import { FaCheck } from "react-icons/fa"
import { useDispatch, useSelector } from "react-redux"
import { useAuth0 } from "@auth0/auth0-react"
import { getAssignmentsForNurseRemoval } from "../../hooks/usePatientReassignments"
import {
	setOptimizeData,
	setIsEditing,
	transferPatientsBetweenNursesAndChairs,
	markNurseAsEdited,
	updateSchedule
} from "../../features/schedule/scheduleSlice"
import {
	setTomorrowData,
	transferPatientsBetweenNursesAndChairsTomorrow,
	markNurseAsEditedTomorrow,
	updateTomorrowSchedule
} from "../../features/schedule/tomorrowSlice"
import {
	transferPatientsBetweenNursesAndChairsSandbox,
	updateSandboxSchedule
} from "../../features/schedule/sandboxSlice"
import store from "../../store"
import DropdownCombobox from "../DailyCalendarFiles/CustomCalendar/FormElements/DropdownCombobox"
import {
	getDateFormatted,
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow,
	timeStringToMinutes,
	convertMinutesToMilitaryTime,
	getClinicTimes
} from "../../utils/generalUtils"
import { getNursePatients } from "../../features/schedule/derivedAssignments"
import {
	calculateNurseAcuity,
	calculateTotalAcuity,
	calculateTotalPatients
} from "../../utils/realTimeNurseUtils"
import { usePostHog } from "posthog-js/react"
import PatientReassignmentConfirmation from "../DailyCalendarFiles/CustomCalendar/PatientReassignmentConfirmation"
import { getScheduleSelection } from "../DailyCalendarFiles/CustomCalendar/scheduleSelection"
import { getScheduleFromMongo } from "../../utils/mongoUtils"
import { useSchedulePatch } from "../../hooks/useSchedulePatch"
import { PiWarningCircleFill } from "react-icons/pi"
import useLogging from "../../hooks/useLogging"
import useFetchSettings from "../../hooks/useFetchSettings"

const EditNursePopupInner = ({ nurse, isOpen, onClose, patientSchedule, setNotification }) => {
	const posthog = usePostHog()
	const { user } = useAuth0()
	const dispatch = useDispatch()
	const { getAccessTokenSilently } = useAuth0()

	const logging = useLogging()
	const isEditingNurse = useSelector(state => state.schedule.isEditing)

	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const schedulePatch = useSchedulePatch({ isTomorrowMode, sandbox })
	const { settingsData } = useFetchSettings()

	// Read-only metrics derive directly from the nurse + Redux schedule — no
	// useState, no sync effect. The outer wrapper keys on nurse.nurseId so
	// switching nurses remounts with fresh state.
	const initialStart = convertMinutesToMilitaryTime(nurse.scheduledStartTime)
	const initialEnd = convertMinutesToMilitaryTime(nurse.scheduledEndTime)
	const initialLunchStart = convertMinutesToMilitaryTime(nurse.scheduledLunchBreakStart)
	const initialLunchEnd = convertMinutesToMilitaryTime(nurse.scheduledLunchBreakEnd)

	const totalAcuity = calculateTotalAcuity(nurse, patientSchedule)
	const totalPatients = calculateTotalPatients(nurse, patientSchedule)
	const currentAcuity = calculateNurseAcuity(
		[nurse],
		patientSchedule,
		undefined,
		undefined,
		sandbox,
		isTomorrowMode
	)[0].currentAcuity

	const isExistingNurse = useMemo(() => {
		if (!settingsData?.nurses) return true
		return Object.values(settingsData.nurses).some(n => n.nurseId === nurse.nurseId)
	}, [settingsData?.nurses, nurse.nurseId])

	const availableNurses = useMemo(() => {
		if (!settingsData?.nurses) return []
		const currentState = isTomorrowMode
			? store.getState().tomorrow
			: sandbox
				? store.getState().sandbox
				: store.getState().schedule
		return Object.values(settingsData.nurses).filter(
			n => !currentState.nurseSchedule.some(cn => cn.nurseId === n.nurseId)
		)
	}, [settingsData?.nurses, isTomorrowMode, sandbox])

	// Editable fields. The wrapper guarantees a fresh mount per nurse, so
	// useState initializers from props are correct without a sync effect.
	const [nurseName, setNurseName] = useState(nurse.nurseName)
	const [scheduledStartTime, setScheduledStartTime] = useState(initialStart)
	const [scheduledEndTime, setScheduledEndTime] = useState(initialEnd)
	const [scheduledLunchBreakStart, setScheduledLunchBreakStart] = useState(initialLunchStart)
	const [scheduledLunchBreakEnd, setScheduledLunchBreakEnd] = useState(initialLunchEnd)

	const [validationErrors, setValidationErrors] = useState({})
	const [warnings, setWarnings] = useState({})
	const [reassignments, setReassignments] = useState([])
	const [isTransitionAcceptOpen, setIsTransitionAcceptOpen] = useState(false)
	const [editedProperties, setEditedProperties] = useState([])
	const [isSaving, setIsSaving] = useState(false)

	const scheduleSelection = getScheduleSelection(isTomorrowMode, sandbox)

	// Regular close handler for escape/click-outside
	const handleClose = () => {
		if (!isSaving) {
			// Only reset editing state if we're not in the middle of saving
			dispatch(setIsEditing(false))
		}
		logging.info("EditNursePopup - Popup Closed", {
			component: "EditNursePopup",
			function: "handleClose",
			nurse
		})
		onClose()
	}

	useEffect(() => {
		if (!isOpen) {
			setValidationErrors({})
			setWarnings({})
		}
	}, [isOpen])

	// Original values captured at mount — wrapper key ensures freshness.
	const originalValues = {
		nurseId: nurse.nurseId,
		nurseName: nurse.nurseName,
		scheduledStartTime: initialStart,
		scheduledEndTime: initialEnd,
		scheduledLunchBreakStart: initialLunchStart,
		scheduledLunchBreakEnd: initialLunchEnd
	}

	const hasChanges = () =>
		nurseName !== originalValues.nurseName ||
		scheduledStartTime !== originalValues.scheduledStartTime ||
		scheduledEndTime !== originalValues.scheduledEndTime ||
		scheduledLunchBreakStart !== originalValues.scheduledLunchBreakStart ||
		scheduledLunchBreakEnd !== originalValues.scheduledLunchBreakEnd

	const settings = settingsData
	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []

	const nextDate = getDateFormatted(
		getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
	)
	const date = isTomorrowMode
		? nextDate
		: getDateFormatted(getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates))

	const targetDate = isTomorrowMode
		? getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
		: getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)

	const { openTime, closeTime } = getClinicTimes(settings, targetDate)
	const lunchBreakStart = timeStringToMinutes(settings?.break_start_time)
	const lunchBreakEnd = timeStringToMinutes(settings?.break_end_time)
	const breakDuration = settings?.break_duration

	const validateTimes = times => {
		const errors = {}
		const newWarnings = {}
		const { start, end, lunchStart, lunchEnd } = times

		// Required field validations
		if (!start) errors.scheduledStartTime = "Shift start time is required"
		if (!end) errors.scheduledEndTime = "Shift end time is required"
		if (!lunchStart) errors.scheduledLunchBreakStart = "Lunch start time is required"
		if (!lunchEnd) errors.scheduledLunchBreakEnd = "Lunch end time is required"

		if (!start || !end || !lunchStart) return { errors, warnings: newWarnings }

		// Shift time validations
		if (start < openTime - 60) {
			errors.scheduledStartTime = "Shift cannot start before center opens"
		}
		if (end > closeTime + 60) {
			errors.scheduledEndTime = "Shift cannot end after center closes"
		}
		if (start >= end) {
			errors.scheduledEndTime = "Shift end must be after shift start"
		}

		// Lunch break validations
		if (lunchStart < start) {
			errors.scheduledLunchBreakStart = "Lunch cannot start before shift starts"
		}
		if (lunchStart < openTime) {
			errors.scheduledLunchBreakStart = "Lunch cannot start before center opens"
		}
		if (lunchEnd > end) {
			errors.scheduledLunchBreakEnd = "Lunch break cannot end after shift ends"
		}
		if (lunchEnd > closeTime) {
			errors.scheduledLunchBreakEnd = "Lunch break cannot end after center closes"
		}
		if (lunchEnd < lunchStart) {
			errors.scheduledLunchBreakEnd = "Lunch break cannot end before lunch starts"
		}

		// Warnings for lunch break outside recommended hours
		if (lunchStart < lunchBreakStart) {
			newWarnings.scheduledLunchBreakStart =
				"Lunch break starts before recommended lunch period"
		}
		if (lunchEnd > lunchBreakEnd) {
			newWarnings.scheduledLunchBreakEnd = "Lunch break ends after recommended lunch period"
		}
		if (lunchEnd - lunchStart < breakDuration) {
			newWarnings.scheduledLunchBreakEnd = "Lunch break is shorter than recommended"
		}

		if (lunchEnd - lunchStart > breakDuration) {
			newWarnings.scheduledLunchBreakEnd = "Lunch break is longer than recommended"
		}

		return { errors, warnings: newWarnings }
	}

	const handleTimeChange = e => {
		const { name, value } = e.target
		const minutes = timeStringToMinutes(value)

		const times = {
			start:
				name === "scheduledStartTime" ? minutes : timeStringToMinutes(scheduledStartTime),
			end: name === "scheduledEndTime" ? minutes : timeStringToMinutes(scheduledEndTime),
			lunchStart:
				name === "scheduledLunchBreakStart"
					? minutes
					: timeStringToMinutes(scheduledLunchBreakStart),
			lunchEnd:
				name === "scheduledLunchBreakEnd"
					? minutes
					: timeStringToMinutes(scheduledLunchBreakEnd)
		}

		const { errors, warnings } = validateTimes(times)
		setValidationErrors(errors)
		setWarnings(warnings)
		// Already-editing dispatch is a no-op for subscribers but still goes
		// through Redux — skip it when isEditing is already true.
		if (!isEditingNurse) dispatch(setIsEditing(true))

		switch (name) {
			case "scheduledStartTime":
				logging.info("EditNursePopup - scheduledStartTime Edited", {
					component: "EditNursePopup",
					function: "handleTimeChange",
					name: "scheduledStartTime",
					nurse: nurse,
					oldValue: convertMinutesToMilitaryTime(nurse.scheduledStartTime),
					newValue: value
				})
				setScheduledStartTime(value)
				setEditedProperties(prev => [...prev, "scheduledStartTime"])
				break
			case "scheduledEndTime":
				logging.info("EditNursePopup - scheduledEndTime Edited", {
					component: "EditNursePopup",
					function: "handleTimeChange",
					name: "scheduledEndTime",
					nurse: nurse,
					oldValue: convertMinutesToMilitaryTime(nurse.scheduledEndTime),
					newValue: value
				})
				setScheduledEndTime(value)
				setEditedProperties(prev => [...prev, "scheduledEndTime"])
				break
			case "scheduledLunchBreakStart":
				logging.info("EditNursePopup - scheduledLunchBreakStart Edited", {
					component: "EditNursePopup",
					function: "handleTimeChange",
					name: "scheduledLunchBreakStart",
					nurse: nurse,
					oldValue: convertMinutesToMilitaryTime(nurse.scheduledLunchBreakStart),
					newValue: value
				})
				setScheduledLunchBreakStart(value)
				setEditedProperties(prev => [...prev, "scheduledLunchStartTime"])
				break
			case "scheduledLunchBreakEnd":
				logging.info("EditNursePopup - scheduledLunchBreakEnd Edited", {
					component: "EditNursePopup",
					function: "handleTimeChange",
					name: "scheduledLunchBreakEnd",
					nurse: nurse,
					oldValue: convertMinutesToMilitaryTime(nurse.scheduledLunchBreakEnd),
					newValue: value
				})
				setScheduledLunchBreakEnd(value)
				setEditedProperties(prev => [...prev, "scheduledLunchEndTime"])
				break
			default:
				break
		}
	}

	const handleSave = async () => {
		setIsSaving(true)

		// Validate times
		const times = {
			start: timeStringToMinutes(scheduledStartTime),
			end: timeStringToMinutes(scheduledEndTime),
			lunchStart: timeStringToMinutes(scheduledLunchBreakStart),
			lunchEnd: timeStringToMinutes(scheduledLunchBreakEnd)
		}

		const { errors } = validateTimes(times)

		if (Object.keys(errors).length > 0) {
			logging.info("EditNursePopup - validation errors", {
				component: "EditNursePopup",
				function: "handleSave",
				nurse: nurse,
				errors: errors
			})
			setValidationErrors(errors)
			return
		}

		logging.info("EditNursePopup - Starting handleSave", {
			component: "EditNursePopup",
			function: "handleSave",
			nurse: nurse
		})

		// Get current state based on mode
		const currentState = isTomorrowMode
			? store.getState().tomorrow
			: sandbox
				? store.getState().sandbox
				: store.getState().schedule

		posthog.capture("schedule-nurse-edit")

		onClose()

		setNotification({ visible: true, message: "Saving changes...", success: true })
		setTimeout(() => setNotification({ visible: false, message: "" }), 2000)

		try {
			let mergedData
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})
			if (!sandbox) {
				mergedData = await getScheduleFromMongo(
					token,
					date,
					dispatch,
					currentState,
					isTomorrowMode,
					sandbox,
					setTomorrowData,
					setOptimizeData,
					logging
				)
			} else {
				// In sandbox mode, use current state as merged data
				mergedData = {
					patientSchedule: currentState.patientSchedule,
					nurseSchedule: currentState.nurseSchedule,
					chairSchedule: currentState.chairSchedule
				}
			}

			// we need to calculate the overlaps between start time and end time

			// the old start time to new start time
			// the new old time to old end time

			let reassignments = []

			const scheduledStartTimeMinutes = timeStringToMinutes(scheduledStartTime)
			const scheduledEndTimeMinutes = timeStringToMinutes(scheduledEndTime)
			const originalValuesScheduledStartTimeMinutes = timeStringToMinutes(
				originalValues.scheduledStartTime
			)
			const originalValuesScheduledEndTimeMinutes = timeStringToMinutes(
				originalValues.scheduledEndTime
			)

			if (originalValuesScheduledStartTimeMinutes < scheduledStartTimeMinutes) {
				// the old start time to new start time

				reassignments = reassignments.concat(
					getAssignmentsForNurseRemoval(
						nurse,
						mergedData,
						true,
						originalValuesScheduledStartTimeMinutes,
						scheduledStartTimeMinutes,
						settings,
						sandbox,
						isTomorrowMode,
						logging
					)
				)
			}

			if (originalValuesScheduledEndTimeMinutes > scheduledEndTimeMinutes) {
				// the new old time to old end time

				reassignments = reassignments.concat(
					getAssignmentsForNurseRemoval(
						nurse,
						mergedData,
						false,
						scheduledEndTimeMinutes,
						originalValuesScheduledEndTimeMinutes,
						settings,
						sandbox,
						isTomorrowMode,
						logging
					)
				)
			}

			if (reassignments && reassignments.length > 0) {
				setReassignments(reassignments)
				setIsTransitionAcceptOpen(true)
			} else {
				editNurseInfo()
				confirmSave(
					mergedData.patientSchedule,
					mergedData.nurseSchedule,
					mergedData.chairSchedule,
					nurse
				)
			}
			logging.info("EditNursePopup - handleSave completed", {
				component: "EditNursePopup",
				function: "handleSave",
				nurse: nurse
			})
		} catch (error) {
			logging.error("Error fetching or updating schedule:", {
				error: error.message,
				stack: error.stack
			})
		}
	}

	const editNurseInfo = () => {
		const currentState = isTomorrowMode
			? store.getState().tomorrow
			: sandbox
				? store.getState().sandbox
				: store.getState().schedule

		const existingNurse = currentState.nurseSchedule.find(n => n.nurseId === nurse.nurseId)

		const updatedPatientSchedule = currentState.patientSchedule.map(patient => ({ ...patient }))

		// If nurse name was changed, get the selected nurse's details from settings
		let updatedNurse
		if (!isExistingNurse && nurseName !== nurse.nurseName) {
			const selectedNurse = availableNurses.find(n => n.nurseName === nurseName)
			if (selectedNurse) {
				updatedNurse = {
					...existingNurse,
					nurseName: selectedNurse.nurseName,
					nurseId: selectedNurse.nurseId,
					nurseEmail: selectedNurse.nurseEmail,
					scheduledStartTime: timeStringToMinutes(scheduledStartTime),
					scheduledEndTime: timeStringToMinutes(scheduledEndTime),
					scheduledLunchBreakStart: timeStringToMinutes(scheduledLunchBreakStart),
					scheduledLunchBreakEnd: timeStringToMinutes(scheduledLunchBreakEnd)
				}

				getNursePatients(updatedPatientSchedule, existingNurse.nurseId).forEach(patient => {
					patient.assignedNurse = updatedNurse.nurseId
				})
			}
		} else {
			// Use existing nurse with updated schedule
			updatedNurse = {
				...existingNurse,
				scheduledStartTime: timeStringToMinutes(scheduledStartTime),
				scheduledEndTime: timeStringToMinutes(scheduledEndTime),
				scheduledLunchBreakStart: timeStringToMinutes(scheduledLunchBreakStart),
				scheduledLunchBreakEnd: timeStringToMinutes(scheduledLunchBreakEnd)
			}
		}

		// Update nurse schedule
		const updatedNurseSchedule = currentState.nurseSchedule.map(n =>
			n.nurseId === nurse.nurseId ? updatedNurse : n
		)

		if (!sandbox && !isTomorrowMode) {
			dispatch(
				updateSchedule({
					nurseSchedule: updatedNurseSchedule,
					patientSchedule: updatedPatientSchedule
				})
			)
		} else if (isTomorrowMode) {
			dispatch(
				updateTomorrowSchedule({
					nurseSchedule: updatedNurseSchedule,
					patientSchedule: updatedPatientSchedule
				})
			)
		} else {
			dispatch(
				updateSandboxSchedule({
					nurseSchedule: updatedNurseSchedule,
					patientSchedule: updatedPatientSchedule
				})
			)
		}
	}

	const confirmSave = async () => {
		setNotification({ visible: true, message: "Saved!", success: true })
		setTimeout(() => setNotification({ visible: false, message: "" }), 2000)

		const editedNurseId = nurse.nurseId

		if (isTomorrowMode) {
			dispatch(markNurseAsEditedTomorrow({ nurseId: editedNurseId, editedProperties }))
		} else {
			dispatch(markNurseAsEdited({ nurseId: editedNurseId, editedProperties }))
		}

		try {
			await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			await schedulePatch.mutateAsync({ date, setNotification })
		} catch (error) {
			logging.error("Error executing schedule update:", {
				error: error.message,
				stack: error.stack
			})
		}

		setIsEditing(false)
		setIsSaving(false)
		onClose()
	}

	const handleTransitionAccept = async () => {
		setIsTransitionAcceptOpen(false)

		if (!nurse) return

		try {
			logging.info("updated nurse and patient schedule")

			editNurseInfo()

			if (sandbox) {
				dispatch(transferPatientsBetweenNursesAndChairsSandbox(reassignments))
			} else if (isTomorrowMode) {
				dispatch(transferPatientsBetweenNursesAndChairsTomorrow(reassignments))
			} else {
				dispatch(transferPatientsBetweenNursesAndChairs(reassignments))
			}

			onClose()
			confirmSave()
		} catch (error) {
			logging.warn("Error executing reassignment:", {
				error: error.message,
				stack: error.stack
			})
		}

		setTimeout(() => setNotification({ visible: false, message: "" }), 2000)
	}

	const handleTransitionDecline = () => {
		setNotification({
			visible: true,
			message:
				"The updated timings will place patients outside of the nurse's shift. Please reassign those patients to another team member.",
			success: false
		})
		setTimeout(() => setNotification({ visible: false, message: "" }), 8000)
		setIsTransitionAcceptOpen(false)
		dispatch(setIsEditing(false))
		setIsSaving(false)
		setReassignments([])
	}

	return (
		<>
			<Popup
				open={isOpen}
				onClose={handleClose}
				modal
				nested
				contentStyle={{
					width: "90%",
					maxWidth: "600px",
					border: "none",
					borderRadius: "10px",
					padding: "0"
				}}
				overlayStyle={{ background: "rgba(0, 0, 0, 0.7)" }}
			>
				<div className="bg-white rounded-lg shadow-lg p-6 max-w-lg mx-auto">
					<div className="flex justify-between items-center mb-4">
						<h2 className="text-xl font-semibold text-custom-blue">
							Edit Nurse Information
						</h2>
					</div>
					<form className="space-y-4">
						{/* Nurse Name */}
						<div>
							<label className="block text-sm font-medium text-gray-700">
								Nurse Name{" "}
								{isExistingNurse ? "" : <span className="text-red-500">*</span>}
							</label>
							{isExistingNurse ? (
								<input
									type="text"
									value={nurseName}
									className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-blue focus:ring-custom-blue sm:text-sm"
									disabled
								/>
							) : (
								<DropdownCombobox
									label="Select Nurse..."
									options={availableNurses}
									value={nurseName}
									onChange={value => {
										const selectedNurse = availableNurses.find(
											n => n.nurseName === value
										)
										if (!isEditingNurse) dispatch(setIsEditing(true))
										setNurseName(selectedNurse?.nurseName || "")
									}}
									displayKey={nurse => nurse.nurseName}
									valueKey="nurseName"
									defaultValue=""
									className="focus:border-custom-blue focus:ring-custom-blue"
									sandbox={sandbox}
								/>
							)}
							{validationErrors.nurseName && (
								<div className="flex items-center mt-1">
									<PiWarningCircleFill
										aria-hidden="true"
										className="h-5 w-5 text-red-500"
									/>
									<p className="text-red-500 ml-2" id="nurseName-error">
										{validationErrors.nurseName}
									</p>
								</div>
							)}
						</div>

						{/* Acuity and Patient Info */}
						<div className="grid grid-cols-3 gap-4">
							<div className="border border-gray-300 rounded-md p-4 shadow-sm">
								<div className="flex items-center">
									<span className="text-blue-500 mr-2">
										<i className="fas fa-heartbeat"></i>
									</span>
									<div>
										<p className="text-sm font-medium text-gray-700">
											Current Acuity
										</p>
										<span className="text-lg font-semibold text-gray-900">
											{currentAcuity}
										</span>
									</div>
								</div>
							</div>
							<div className="border border-gray-300 rounded-md p-4 shadow-sm">
								<div className="flex items-center">
									<span className="text-green-500 mr-2">
										<i className="fas fa-user-friends"></i>
									</span>
									<div>
										<p className="text-sm font-medium text-gray-700">
											Total Patients
										</p>
										<span className="text-lg font-semibold text-gray-900">
											{totalPatients}
										</span>
									</div>
								</div>
							</div>
							<div className="border border-gray-300 rounded-md p-4 shadow-sm">
								<div className="flex items-center">
									<span className="text-red-500 mr-2">
										<i className="fas fa-chart-line"></i>
									</span>
									<div>
										<p className="text-sm font-medium text-gray-700">
											Total Acuity
										</p>
										<span className="text-lg font-semibold text-gray-900">
											{totalAcuity}
										</span>
									</div>
								</div>
							</div>
						</div>

						{/* Shift and Lunch Times */}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700">
									Shift Start Time <span className="text-red-500">*</span>
								</label>
								<input
									type="time"
									name="scheduledStartTime"
									value={scheduledStartTime}
									onChange={handleTimeChange}
									className={`mt-1 block w-full rounded-md ${
										validationErrors.scheduledStartTime
											? "border-red-500"
											: "border-gray-300"
									} shadow-sm focus:border-custom-blue focus:ring-custom-blue sm:text-sm`}
								/>
								{validationErrors.scheduledStartTime && (
									<div className="flex items-center mt-1">
										<PiWarningCircleFill
											aria-hidden="true"
											className="h-5 w-5 text-red-500"
										/>
										<p
											className="text-red-500 ml-2"
											id="scheduledStartTime-error"
										>
											{validationErrors.scheduledStartTime}
										</p>
									</div>
								)}
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700">
									Shift End Time <span className="text-red-500">*</span>
								</label>
								<input
									type="time"
									name="scheduledEndTime"
									value={scheduledEndTime}
									onChange={handleTimeChange}
									className={`mt-1 block w-full rounded-md ${
										validationErrors.scheduledEndTime
											? "border-red-500"
											: "border-gray-300"
									} shadow-sm focus:border-custom-blue focus:ring-custom-blue sm:text-sm`}
								/>
								{validationErrors.scheduledEndTime && (
									<div className="flex items-center mt-1">
										<PiWarningCircleFill
											aria-hidden="true"
											className="h-5 w-5 text-red-500"
										/>
										<p
											className="text-red-500 ml-2"
											id="scheduledEndTime-error"
										>
											{validationErrors.scheduledEndTime}
										</p>
									</div>
								)}
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700">
									Lunch Start Time <span className="text-red-500">*</span>
								</label>
								<input
									type="time"
									name="scheduledLunchBreakStart"
									value={scheduledLunchBreakStart}
									onChange={handleTimeChange}
									className={`mt-1 block w-full rounded-md ${
										validationErrors.scheduledLunchBreakStart
											? "border-red-500"
											: "border-gray-300"
									} shadow-sm focus:border-custom-blue focus:ring-custom-blue sm:text-sm`}
								/>
								{validationErrors.scheduledLunchBreakStart && (
									<div className="flex items-center mt-1">
										<PiWarningCircleFill
											aria-hidden="true"
											className="h-5 w-5 text-red-500"
										/>
										<p
											className="text-red-500 ml-2"
											id="scheduledStartTime-error"
										>
											{validationErrors.scheduledLunchBreakStart}
										</p>
										{warnings.scheduledLunchBreakStart &&
											!validationErrors.scheduledLunchBreakStart && (
												<p className="text-red-600 text-sm mt-1">
													{warnings.scheduledLunchBreakStart}
												</p>
											)}
									</div>
								)}
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700">
									Lunch End Time <span className="text-red-500">*</span>
								</label>
								<input
									type="time"
									name="scheduledLunchBreakEnd"
									value={scheduledLunchBreakEnd}
									onChange={handleTimeChange}
									className={`mt-1 block w-full rounded-md ${
										validationErrors.scheduledLunchBreakEnd
											? "border-red-500"
											: "border-gray-300"
									} shadow-sm focus:border-custom-blue focus:ring-custom-blue sm:text-sm`}
								/>
								{validationErrors.scheduledLunchBreakEnd && (
									<div className="flex items-center mt-1">
										<PiWarningCircleFill
											aria-hidden="true"
											className="h-5 w-5 text-red-500"
										/>
										<p
											className="text-red-500 ml-2"
											id="scheduledEndTime-error"
										>
											{validationErrors.scheduledLunchBreakEnd}
										</p>
										{warnings.scheduledLunchBreakEnd &&
											!validationErrors.scheduledLunchBreakEnd && (
												<p className="text-red-600 text-sm mt-1">
													{warnings.scheduledLunchBreakEnd}
												</p>
											)}
									</div>
								)}
							</div>
						</div>
					</form>

					<div className="flex justify-end mt-6">
						<button
							onClick={handleSave}
							className={`bg-customBlue text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-customBlue-dark transition duration-150 flex items-center ${
								!hasChanges() || Object.keys(validationErrors).length > 0
									? "opacity-50 cursor-not-allowed"
									: ""
							}`}
							disabled={!hasChanges() || Object.keys(validationErrors).length > 0}
						>
							<FaCheck className="mr-2" /> <span>Update Nurse</span>
						</button>
					</div>
				</div>
			</Popup>

			<PatientReassignmentConfirmation
				title="Patient Reassignments Required to Edit Shift"
				message={`The following patient reassignments are suggested based on current schedule:`}
				isOpen={isTransitionAcceptOpen}
				onAccept={handleTransitionAccept}
				onDecline={handleTransitionDecline}
				reassignments={reassignments}
				scheduleSelection={scheduleSelection}
				remove={false}
			/>
		</>
	)
}

// Re-mount on nurse change so the editable form state re-initializes from
// props without a sync effect. Callers must only render this when nurse is set.
const EditNursePopup = (props) => (
	<EditNursePopupInner key={props.nurse.nurseId} {...props} />
)

export default EditNursePopup
